
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION_NAME = 'vehicle_assignments';
export const getVehicleAssignments = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('start_date', 'desc'));
    const querySnapshot = await getDocs(q);
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({
        assignment_id: doc.id,
        ...doc.data()
      });
    });
    return assignments;
  } catch (error) {
    console.error('Error getting vehicle assignments: ', error);
    throw error;
  }
};

export const subscribeToAssignments = (callback) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('start_date', 'desc'));
  return onSnapshot(q, (querySnapshot) => {
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({
        assignment_id: doc.id,
        ...doc.data()
      });
    });
    callback(assignments);
  }, (error) => {
    console.error('Error in assignments subscription: ', error);
    callback([]);
  });
};

export const createAssignment = async (assignmentData) => {
  try {
    const assignmentDoc = {
      vehicle_id: assignmentData.vehicle_id,
      driver_id: assignmentData.driver_id,
      start_date: assignmentData.start_date,
      end_date: assignmentData.end_date,
      status: 'active',
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), assignmentDoc);
    return {
      assignment_id: docRef.id,
      ...assignmentDoc
    };
  } catch (error) {
    console.error('Error creating assignment: ', error);
    throw error;
  }
};

export const updateAssignment = async (assignmentId, assignmentData) => {
  try {
    const assignmentRef = doc(db, COLLECTION_NAME, assignmentId);
    const updateData = {
      ...assignmentData,
      updated_at: serverTimestamp()
    };
    await updateDoc(assignmentRef, updateData);
    return { assignment_id: assignmentId, ...updateData };
  } catch (error) {
    console.error('Error updating assignment: ', error);
    throw error;
  }
};

export const deleteAssignment = async (assignmentId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, assignmentId));
    return true;
  } catch (error) {
    console.error('Error deleting assignment: ', error);
    throw error;
  }
};

export const getVehicleAssignment = async (vehicleId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('vehicle_id', '==', vehicleId),
      where('status', '==', 'active'),
      orderBy('start_date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        assignment_id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting vehicle assignment: ', error);
    throw error;
  }
};

export const getDriverAssignments = async (driverId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME), 
      where('driver_id', '==', driverId),
      where('status', '==', 'active'),
      orderBy('start_date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const assignments = [];
    querySnapshot.forEach((doc) => {
      assignments.push({
        assignment_id: doc.id,
        ...doc.data()
      });
    });
    return assignments;
  } catch (error) {
    console.error('Error getting driver assignments: ', error);
    throw error;
  }
};

// Remove assignment for a vehicle (sets status to inactive)
export const removeVehicleAssignment = async (vehicleId) => {
  try {
    const currentAssignment = await getVehicleAssignment(vehicleId);
    if (currentAssignment) {
      await updateAssignment(currentAssignment.assignment_id, {
        status: 'inactive',
        end_date: new Date().toISOString().split('T')[0] // Set end date to today
      });
    }
    return true;
  } catch (error) {
    console.error('Error removing vehicle assignment: ', error);
    throw error;
  }
};

// Check if a driver is currently assigned to any vehicle
export const isDriverAssigned = async (driverId) => {
  try {
    const assignments = await getDriverAssignments(driverId);
    return assignments.length > 0;
  } catch (error) {
    console.error('Error checking driver assignment: ', error);
    return false;
  }
};